import React, { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Container, Typography, Link as MuiLink } from "@mui/material";
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { ThemeContext } from "components/Theme/ThemeContext";
import { useTranslation } from "react-i18next";

export default function PrivacyPolicy(props) {
  const { isDark, currentTheme } = useContext(ThemeContext);
  const { i18n } = useTranslation();
  const isRTL = i18n?.language === 'ar' || i18n?.language === 'ara' || (typeof i18n?.language === 'string' && i18n.language.startsWith('ar'));
  const { ...rest } = props;

  return (
    <Box sx={{ bgcolor: currentTheme.background, display: 'flex', flexDirection: 'column', transition: 'background-color 0.3s ease, color 0.3s ease', direction: isRTL ? 'rtl' : 'ltr' }}>
      <Helmet>
        <title>{isRTL ? 'سياسة الخصوصية | زحل قطر' : 'Privacy Policy | Rahl Qatar'}</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Header
          color="transparent"
          routes={[]}
          rightLinks={<HeaderLinks />}
          fixed
          changeColorOnScroll={{
            height: 100,
            color: isDark ? "dark" : "white"
          }}
          {...rest}
        />

        {/* Hero Section */}
        <Box sx={{
          background: isDark ? 'linear-gradient(135deg, #0A0A0B 0%, #161619 100%)' : 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
          color: isDark ? 'white' : '#0F172A',
          pt: 20,
          pb: 8,
          textAlign: 'center'
        }}>
          <Container maxWidth="md">
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontFamily: 'Outfit, sans-serif' }}>
              {isRTL ? 'سياسة الخصوصية وحماية البيانات' : 'Privacy Policy & Data Protection'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.7, color: isDark ? '#94a3b8' : '#475569' }}>
              {isRTL ? 'تاريخ السريان: 1 أغسطس 2026 • آخر تحديث: أغسطس 2026' : 'Effective Date: August 1, 2026 • Last Updated: August 2026'}
            </Typography>
          </Container>
        </Box>

        {/* Content Section */}
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box sx={{ color: isDark ? '#cbd5e1' : '#334155', textAlign: isRTL ? 'right' : 'left', '& p': { mb: 3, lineHeight: 1.8 }, '& h3': { mt: 4, mb: 2, fontWeight: 700, color: isDark ? 'white' : '#0f172a' } }}>

            <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 4 }}>
              {isRTL
                ? 'متوافقة مع قانون دولة قطر رقم 13 لسنة 2016 بشأن حماية خصوصية البيانات الشخصية (PDPPL) ومعايير الخصوصية المعمول بها في دول مجلس التعاون الخليجي.'
                : 'Compliant with Qatar Law No. 13 of 2016 (Personal Data Privacy Protection Law – PDPPL) and applicable GCC privacy standards.'}
            </Typography>

            {isRTL ? (
              <>
                <Typography variant="h6" component="h3">
                  1. مقدمة وتعريف مسؤول البيانات
                </Typography>
                <Typography variant="body1">
                  توضح سياسة الخصوصية هذه ممارسات شركة "آي ويفز لحلول تكنولوجيا المعلومات" ("الشركة" أو "نحن" أو "لنا")، وهي كيان مسجل في مدينة الدوحة، دولة قطر، فيما يتعلق بجمع البيانات الشخصية واستخدامها والإفصاح عنها وحمايتها من خلال منصة "زحل™" الذكية لسلامة المركبات، والبوابات الإلكترونية عبر الجوال، وواجهات رموز الاستجابة السريعة (QR) الديناميكية، والمنتجات المادية المرتبطة بها (يُشار إليها مجتمعة بـ "المنصة").
                </Typography>
                <Typography variant="body1">
                  لأغراض قانون دولة قطر رقم 13 لسنة 2016 (قانون حماية خصوصية البيانات الشخصية - PDPPL) ومعايير الخصوصية المعمول بها في دول مجلس التعاون الخليجي، تعمل شركة آي ويفز لحلول تكنولوجيا المعلومات بصفتها مسؤول البيانات (Data Controller).
                </Typography>

                <Typography variant="h6" component="h3">
                  2. التعريفات
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>"البيانات الشخصية":</strong> أي بيانات تتعلق بشخص طبيعي يمكن تحديد هويته بشكل مباشر أو غير مباشر.</li>
                  <li><strong>"صاحب البيانات" / "المستخدم":</strong> أي شخص طبيعي يستخدم المنصة، بما في ذلك مالكو المركبات المسجلين ("أصحاب الحسابات") والأفراد من الجمهور الذين يقومون بمسح درع زحل الذكي للسلامة ("المارّة/الماسحون").</li>
                  <li><strong>"نظام التوجيه المُقنَّع":</strong> التقنية الحصرية التي توجه المكالمات الصوتية والرسائل النصية عبر أرقام وكيلة افتراضية مشفّرة دون كشف أرقام الهاتف الشخصية لأي من الطرفين.</li>
                  <li><strong>"الجهة المختصة":</strong> الهيئة الوطنية للأمن السيبراني (NCGAA) / وزارة الاتصالات وتكنولوجيا المعلومات (MCIT) في دولة قطر.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  3. المبادئ الأساسية لحماية البيانات
                </Typography>
                <Typography variant="body1">
                  نلتزم التزاماً صارماً بالمبادئ الأساسية المنصوص عليها في المادة 3 من قانون حماية خصوصية البيانات الشخصية القطري (PDPPL):
                </Typography>
                <Box component="ol" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>الشفافية والنزاهة:</strong> تُعالَج البيانات الشخصية بشكل قانوني وعادل وشفاف.</li>
                  <li><strong>تحديد الغرض:</strong> تُجمع البيانات حصراً لأغراض صريحة ومشروعة تتعلق بسلامة المركبات والتواصل.</li>
                  <li><strong>تقليل البيانات:</strong> نجمع فقط الحد الأدنى من البيانات اللازمة لتشغيل خدمة الاتصال المُقنَّعة.</li>
                  <li><strong>المعالجة العابرة:</strong> تُعد مدخلات المارّة وسجلات توجيه المكالمات بيانات مؤقتة تُحذف فور انتهاء الجلسة.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  4. البيانات التي نجمعها
                </Typography>
                <Typography variant="body1">
                  <strong>أ. مالكو المركبات المسجلون (أصحاب الحسابات)</strong>
                </Typography>
                <Typography variant="body1">
                  لتفعيل وإدارة درع زحل الذكي للسلامة، نقوم بجمع:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>بيانات الهوية:</strong> الاسم الكامل، اللغة المفضلة.</li>
                  <li><strong>بيانات الاتصال:</strong> رقم الهاتف المحمول الموثّق، البريد الإلكتروني.</li>
                  <li><strong>بيانات المركبة:</strong> ماركة المركبة وطرازها ورقم لوحة الترخيص (تُستخدم حصراً للتحقق من ملكية العلامة ومنع إساءة الاستخدام الاحتيالية).</li>
                  <li><strong>بيانات اعتماد الحساب:</strong> كلمات المرور المشفرة بأمان، ورموز المصادقة.</li>
                  <li><strong>بيانات المعاملات:</strong> بيانات وصفية لتأكيد الدفع (تُعالج عبر بوابات دفع من جهات خارجية معتمدة ومتوافقة مع معيار PCI-DSS؛ ولا نقوم بتخزين تفاصيل بطاقات الائتمان الكاملة على خوادمنا).</li>
                </Box>
                <Typography variant="body1">
                  <strong>ب. المارّة/الماسحون من الجمهور (مستخدمون غير مسجلين)</strong>
                </Typography>
                <Typography variant="body1">
                  عند قيام أحد أفراد الجمهور بمسح رمز QR الخاص بزحل:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>عدم الحاجة إلى حساب:</strong> لا يُطلب من الماسح تحميل تطبيق أو إنشاء حساب أو تقديم بيانات ملف شخصي.</li>
                  <li><strong>البيانات الوصفية للجلسة:</strong> عنوان IP مؤقت، ونص وكيل جهاز المستخدم (user-agent)، والطابع الزمني، والموقع الجغرافي (فقط إذا منح الماسح صراحةً إذن الموقع من المتصفح للإبلاغ عن خطر دقيق متعلق بالمركبة).</li>
                  <li><strong>مدخلات التواصل:</strong> قوالب التنبيه المحددة مسبقاً المختارة (مثل "المركبة تعيق الممر"، "الأضواء مُشغّلة"، "جهة اتصال طارئة") أو بيانات بث صوتي مؤقتة أثناء المكالمات المُقنَّعة.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  5. الأساس القانوني للمعالجة والموافقة
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>أصحاب الحسابات:</strong> تستند المعالجة إلى الموافقة الصريحة المقدمة أثناء تسجيل الحساب وتفعيل العلامة (المادة 4 من PDPPL)، وكذلك إلى تنفيذ العقد (تقديم خدمة زحل).</li>
                  <li><strong>المارّة/الماسحون:</strong> تستند المعالجة إلى الغرض المشروع المتمثل في تقديم إشعار سلامة تحذيري مؤقت لمالك المركبة بناءً على مبادرة الماسح الطوعية.</li>
                </Box>
                <Typography variant="body1">
                  يحتفظ أصحاب الحسابات بالحق في سحب موافقتهم في أي وقت عبر بوابة إعدادات حساب زحل، وهو ما سيؤدي إلى إلغاء تفعيل درع السلامة الذكي المرتبط.
                </Typography>

                <Typography variant="h6" component="h3">
                  6. البنية التقنية: الاتصالات المُقنَّعة وانعدام كشف البيانات الشخصية
                </Typography>
                <Typography variant="body1">
                  صُممت منصة زحل وفق مبدأ "الخصوصية بالتصميم":
                </Typography>
                <Box component="ol" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>عدم العرض العلني:</strong> لا تُعرض أرقام الهواتف أو الأسماء الكاملة أو البيانات الشخصية لمالك المركبة مطلقاً على البوابة الإلكترونية أو في الشيفرة المصدرية.</li>
                  <li><strong>وكلاء اتصال مشفّرون:</strong> عندما ينقر الماسح على "اتصال بالمالك" أو "إرسال رسالة نصية"، يقوم خادمنا بإنشاء جسر افتراضي آمن. يقوم مزود الاتصالات بربط الطرفين عبر رقم جسر وكيل.</li>
                  <li><strong>عدم تسجيل الصوت:</strong> المكالمات الصوتية هي بث مؤقت وعابر. لا تقوم شركة آي ويفز لحلول تكنولوجيا المعلومات بتسجيل أو تخزين أو مراقبة أو التنصت على محتوى المكالمات الصوتية.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  7. سياسة الاحتفاظ بالبيانات وتخزينها
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>بيانات الحساب:</strong> تُحفظ طوال مدة الاشتراك النشط، بالإضافة إلى ثلاثين (30) يوماً بعد إنهاء الحساب، ثم تُحذف نهائياً أو تُخفى هويتها.</li>
                  <li><strong>البيانات الوصفية للاتصالات:</strong> تُحفظ سجلات الاتصال (الطوابع الزمنية، مدة المكالمة، معرّفات التوجيه المُقنَّع) لمدة تصل إلى تسعين (90) يوماً لأغراض التشخيص التقني، ومكافحة الاحتيال، والامتثال الأمني فقط، ثم تُحذف تلقائياً.</li>
                  <li><strong>سجلات جلسات المارّة:</strong> تُحذف عناوين IP ورموز الجلسة المؤقتة خلال ثمانٍ وأربعين (48) ساعة.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  8. مشاركة البيانات والجهات المعالجة من الأطراف الثالثة
                </Typography>
                <Typography variant="body1">
                  نحن لا نبيع أو نؤجر أو نتاجر بالبيانات الشخصية أو نحوّلها إلى مصدر ربح. تُشارَك البيانات حصراً مع جهات معالجة ملتزمة بموجب اتفاقيات معالجة بيانات صارمة (DPAs):
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>مزودو البنية التحتية للاتصالات:</strong> لتقديم خدمات الرسائل النصية والمكالمات الصوتية عبر الإنترنت (VoIP) المُقنَّعة.</li>
                  <li><strong>مزودو الاستضافة السحابية:</strong> مراكز بيانات آمنة ومعتمدة بشهادة الأيزو 27001، موجودة داخل دولة قطر أو ضمن الولايات القضائية المعتمدة في دول مجلس التعاون الخليجي، بما يضمن الامتثال لمتطلبات إقامة البيانات.</li>
                  <li><strong>سلطات إنفاذ القانون والجهات القانونية:</strong> نُفصح عن البيانات الشخصية فقط عند إلزامنا بذلك بموجب أمر قانوني صحيح، أو استدعاء قضائي، أو طلب رسمي صادر عن السلطات القضائية أو الأمنية القطرية المختصة بموجب القانون القطري.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  9. نقل البيانات عبر الحدود
                </Typography>
                <Typography variant="body1">
                  وفقاً للمادة 15 من قانون حماية خصوصية البيانات الشخصية القطري، تُخزَّن البيانات الشخصية التي تعالجها شركة آي ويفز لحلول تكنولوجيا المعلومات على بنية تحتية أساسية موجودة داخل دولة قطر أو ضمن الولايات القضائية لدول مجلس التعاون الخليجي التي توفر معايير حماية بيانات مكافئة. ويخضع أي نقل عابر للحدود لأغراض الدعم التشغيلي لتقييم أثر صارم لضمان الامتثال الكامل.
                </Typography>

                <Typography variant="h6" component="h3">
                  10. حقوق صاحب البيانات (بموجب قانون PDPPL القطري)
                </Typography>
                <Typography variant="body1">
                  بموجب القانون رقم 13 لسنة 2016، يتمتع المستخدمون بالحقوق القانونية التالية:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>الحق في الاطلاع:</strong> طلب تأكيد ونسخ من البيانات الشخصية التي نحتفظ بها.</li>
                  <li><strong>الحق في التصحيح/الحذف:</strong> طلب تصحيح البيانات الشخصية غير الدقيقة أو الناقصة أو القديمة.</li>
                  <li><strong>الحق في المحو ("الحق في النسيان"):</strong> طلب حذف البيانات الشخصية عندما لا تعود المعالجة ضرورية أو عند سحب الموافقة.</li>
                  <li><strong>الحق في الاعتراض:</strong> الاعتراض على المعالجة إذا كانت غير ضرورية أو تمييزية.</li>
                </Box>
                <Typography variant="body1">
                  لممارسة هذه الحقوق، يُرجى تقديم طلب خطي إلى مسؤول حماية البيانات لدينا عبر البريد الإلكتروني: support@rahl.me. تُعالَج الطلبات خلال ثلاثين (30) يوماً تقويمياً.
                </Typography>

                <Typography variant="h6" component="h3">
                  11. إدارة خرق البيانات
                </Typography>
                <Typography variant="body1">
                  في حال حدوث خرق مؤكد للبيانات الشخصية يهدد خصوصية الأفراد أو أمن بياناتهم، ستقوم شركة آي ويفز لحلول تكنولوجيا المعلومات، عملاً بالمادة 12 من قانون PDPPL، بإخطار الهيئة الوطنية للأمن السيبراني (NCGAA) وأصحاب البيانات المتأثرين ضمن الأطر الزمنية القانونية المحددة، مع تفصيل طبيعة الخرق والإجراءات العلاجية المتخذة.
                </Typography>

                <Typography variant="h6" component="h3">
                  12. بيانات التواصل
                </Typography>
                <Typography variant="body1">
                  <strong>مسؤول حماية البيانات (DPO)</strong>
                  <br />
                  شركة آي ويفز لحلول تكنولوجيا المعلومات
                  <br />
                  الدوحة، دولة قطر
                  <br />
                  <strong>البريد الإلكتروني:</strong> <MuiLink href="mailto:support@rahl.me" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>support@rahl.me</MuiLink>
                  <br />
                  <strong>الهاتف:</strong> <MuiLink href="tel:+97431395694" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>+974 31395694</MuiLink>
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" component="h3">
                  1. Introduction &amp; Controller Identification
                </Typography>
                <Typography variant="body1">
                  This Privacy Policy outlines the practices of iWaves IT Solution Services ("Company," "we," "us," or "our"), a registered entity in Doha, State of Qatar, regarding the collection, use, disclosure, and protection of personal data through the Rahl™ smart vehicle safety platform, mobile web portals, dynamic QR interfaces, and associated hardware products (collectively, the "Platform").
                </Typography>
                <Typography variant="body1">
                  For the purposes of Qatar Law No. 13 of 2016 (Personal Data Privacy Protection Law - PDPPL) and applicable GCC privacy standards, iWaves IT Solution Services acts as the Data Controller.
                </Typography>

                <Typography variant="h6" component="h3">
                  2. Definitions
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>"Personal Data":</strong> Any data relating to a natural person whose identity is identified or can reasonably be identified, directly or indirectly.</li>
                  <li><strong>"Data Subject" / "User":</strong> Any natural person accessing the Platform, including registered vehicle owners ("Account Holders") and public individuals who scan a Rahl Smart Safety Shield ("Bystanders/Scanners").</li>
                  <li><strong>"Masked Routing System":</strong> The proprietary technology that routes voice calls and SMS through encrypted virtual proxy numbers without exposing the personal phone numbers of either party.</li>
                  <li><strong>"Competent Authority":</strong> The National Cyber Governance and Assurance Affairs (NCGAA) / Ministry of Communications and Information Technology (MCIT) in Qatar.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  3. Core Principles of Data Protection
                </Typography>
                <Typography variant="body1">
                  We adhere strictly to the fundamental principles established under Article 3 of the Qatar PDPPL:
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Transparency &amp; Fairness:</strong> Personal data is processed lawfully, fairly, and transparently.</li>
                  <li><strong>Purpose Limitation:</strong> Data is collected solely for explicit, legitimate vehicle safety and communication purposes.</li>
                  <li><strong>Data Minimization:</strong> We collect only the minimum data necessary to operate our masked communication service.</li>
                  <li><strong>Ephemeral Processing:</strong> Bystander inputs and call routing logs are transient and purged upon session completion.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  4. Information We Collect
                </Typography>
                <Typography variant="body1">
                  <strong>A. Registered Vehicle Owners (Account Holders)</strong>
                </Typography>
                <Typography variant="body1">
                  To activate and manage a Rahl Smart Safety Shield, we collect:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Identity Data:</strong> Full Name, Preferred Language.</li>
                  <li><strong>Contact Data:</strong> Verified Mobile Phone Number, Email Address.</li>
                  <li><strong>Vehicle Data:</strong> Vehicle Make, Model, and License Plate Number (used strictly to verify tag ownership and prevent anti-fraud misuse).</li>
                  <li><strong>Account Credentials:</strong> Securely hashed passwords, authentication tokens.</li>
                  <li><strong>Transaction Data:</strong> Payment confirmation metadata (processed via PCI-DSS compliant third-party payment gateways; we do not store full credit card details on our servers).</li>
                </Box>
                <Typography variant="body1">
                  <strong>B. Public Bystanders / Scanners (Non-Registered Users)</strong>
                </Typography>
                <Typography variant="body1">
                  When a public individual scans a Rahl QR tag:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Zero-Account Requirement:</strong> Scanners are not required to download an app, create an account, or submit personal profile data.</li>
                  <li><strong>Session Metadata:</strong> Temporary IP address, device user-agent string, timestamp, and geo-location (only if the scanner explicitly grants browser location permission to report an exact vehicle hazard).</li>
                  <li><strong>Communication Inputs:</strong> Pre-set alert templates selected (e.g., "Vehicle Blocking Driveway," "Lights ON," "Emergency Contact") or temporary audio stream data during masked calls.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  5. Lawful Basis for Processing &amp; Consent
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Account Holders:</strong> Processing is based on Explicit Consent provided during account registration and tag activation (Article 4, PDPPL), as well as performance of a contract (delivering the Rahl service).</li>
                  <li><strong>Bystanders/Scanners:</strong> Processing is based on the Legitimate Purpose of delivering a temporary courtesy safety notification to the vehicle owner upon the scanner's voluntary initiation.</li>
                </Box>
                <Typography variant="body1">
                  Account Holders retain the right to withdraw consent at any time via the Rahl Account Settings portal, which will deactivate the associated Smart Safety Shield.
                </Typography>

                <Typography variant="h6" component="h3">
                  6. Technical Architecture: Masked Communications &amp; Zero-PII Exposure
                </Typography>
                <Typography variant="body1">
                  Rahl is engineered with a Privacy-by-Design architecture:
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>No Public Display:</strong> Phone numbers, full names, and personal details of the vehicle owner are never rendered on the web portal or exposed in the source code.</li>
                  <li><strong>Encrypted Telephony Proxies:</strong> When a scanner clicks "Call Owner" or "Send SMS", our server invokes a secure virtual bridge. The telecom carrier connects both parties through a proxy bridge number.</li>
                  <li><strong>No Audio Recording:</strong> Voice calls are transient streams. iWaves IT Solution Services does not record, record-store, monitor, or wiretap the content of voice communications.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  7. Data Retention &amp; Storage Policy
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Account Data:</strong> Retained for the duration of the active subscription plus thirty (30) days following account termination, after which it is permanently deleted or anonymized.</li>
                  <li><strong>Communication Metadata:</strong> Telephony logs (timestamps, call duration, masked routing IDs) are retained for up to ninety (90) days solely for network diagnostics, anti-fraud auditing, and security compliance, after which they are automatically purged.</li>
                  <li><strong>Bystander Session Logs:</strong> IP addresses and temporary session tokens are purged within forty-eight (48) hours.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  8. Data Sharing &amp; Third-Party Processors
                </Typography>
                <Typography variant="body1">
                  We do not sell, rent, trade, or monetize personal data. Data is shared exclusively with bound service processors under strict data processing agreements (DPAs):
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Telecom Infrastructure Providers:</strong> For delivering masked SMS and VoIP calls.</li>
                  <li><strong>Cloud Hosting Providers:</strong> Secure, ISO 27001-certified data centers localized within Qatar or approved GCC jurisdictions ensuring data residency compliance.</li>
                  <li><strong>Law Enforcement &amp; Legal Authorities:</strong> We disclose personal data only when compelled by a valid legal order, court summons, or official request issued by competent Qatari Judicial or Security Authorities under Qatari statutory law.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  9. Cross-Border Data Transfers
                </Typography>
                <Typography variant="body1">
                  In accordance with Article 15 of the Qatar PDPPL, personal data processed by iWaves IT Solution Services is stored on primary infrastructure located within the State of Qatar or GCC jurisdictions providing equivalent data protection standards. Any cross-border transfer for operational support undergoes rigorous impact assessment to ensure full compliance.
                </Typography>

                <Typography variant="h6" component="h3">
                  10. Data Subject Rights (Qatar PDPPL)
                </Typography>
                <Typography variant="body1">
                  Under Law No. 13 of 2016, Users possess the following statutory rights:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, lineHeight: 1.8 } }}>
                  <li><strong>Right to Access:</strong> Request confirmation and copies of personal data held by us.</li>
                  <li><strong>Right to Correction/Omission:</strong> Request correction of inaccurate, incomplete, or out-of-date personal data.</li>
                  <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of personal data when processing is no longer necessary or consent is withdrawn.</li>
                  <li><strong>Right to Object:</strong> Object to processing if it is unnecessary or discriminatory.</li>
                </Box>
                <Typography variant="body1">
                  To exercise these rights, submit a written request to our Data Protection Officer at <MuiLink href="mailto:support@rahl.me" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>support@rahl.me</MuiLink>. Requests are processed within thirty (30) calendar days.
                </Typography>

                <Typography variant="h6" component="h3">
                  11. Data Breach Management
                </Typography>
                <Typography variant="body1">
                  In the event of a confirmed personal data breach that threatens individual privacy or data security, iWaves IT Solution Services will, pursuant to Article 12 of the PDPPL, notify the National Cyber Governance and Assurance Affairs (NCGAA) and affected Data Subjects within the statutory timeframes, detailing the nature of the breach and remedial actions taken.
                </Typography>

                <Typography variant="h6" component="h3">
                  12. Contact Details
                </Typography>
                <Typography variant="body1">
                  <strong>Data Protection Officer (DPO)</strong>
                  <br />
                  iWaves IT Solution Services
                  <br />
                  Doha, State of Qatar
                  <br />
                  <strong>Email:</strong> <MuiLink href="mailto:support@rahl.me" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>support@rahl.me</MuiLink>
                  <br />
                  <strong>Telephone:</strong> <MuiLink href="tel:+97431395694" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>+974 31395694</MuiLink>
                </Typography>
              </>
            )}

          </Box>
        </Container>
      </Box>
      <HomeFooter />
    </Box>
  );
}
